<script setup lang="ts">
/**
 * Singleton undo toast — mounted once in AppShell (or directly in App.vue),
 * listens to useUndoToast() state, renders a bottom-center pill with
 * the message + an Undo button.
 */
import { useUndoToast } from '@/composables/useUndoToast';
import { Undo2 } from 'lucide-vue-next';

const { current, triggerUndo, dismiss } = useUndoToast();
</script>

<template>
  <Transition
    enter-active-class="undo-toast-enter-active"
    enter-from-class="opacity-0 translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="undo-toast-leave-active"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-4"
  >
    <div
      v-if="current"
      class="undo-toast fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 px-4 py-2.5 bg-surface-800 border border-border-default rounded-full shadow-2xl"
      data-qa-undo-toast
    >
      <span class="text-sm text-text-primary whitespace-nowrap">{{ current.message }}</span>
      <button
        type="button"
        class="undo-toast-button flex items-center gap-1 px-2 py-1 rounded-full bg-accent-blue/15 text-accent-blue text-xs font-semibold hover:bg-accent-blue/25 cwr-motion"
        data-qa-action="undo"
        @click="triggerUndo"
      >
        <Undo2 class="w-3 h-3" />
        Undo
      </button>
      <button
        type="button"
        class="text-text-disabled hover:text-text-muted text-xs px-1 cwr-motion"
        data-qa-action="dismiss"
        @click="dismiss"
      >
        ×
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.undo-toast-enter-active {
  transition:
    opacity 200ms cubic-bezier(0, 0, 0.2, 1),
    transform 200ms cubic-bezier(0, 0, 0.2, 1);
}
.undo-toast-leave-active {
  transition:
    opacity 150ms cubic-bezier(0.4, 0, 1, 1),
    transform 150ms cubic-bezier(0.4, 0, 1, 1);
}
@media (prefers-reduced-motion: reduce) {
  .undo-toast-enter-active,
  .undo-toast-leave-active {
    transition: none !important;
  }
}
</style>
